import { Box } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import SEO from '@/shared/components/SEO';
import {
  UserContext,
  UserLoadState,
} from '@/shared/contexts/UserContext';
import LoginCard from './LoginForm';
import SignUpForm from './SignUpForm';

function AuthContainer() {
  const location = useLocation();
  const isLogin = location.pathname.includes('sign-in');
  const { userLoadState } = useContext(UserContext);
  const searchParams = new URLSearchParams(location.search);
  const urlSignUpFlag = searchParams.get('suf') ?? '';
  const navigate = useNavigate();

  // Track authentication page visits
  useEffect(() => {
    const authType = isLogin ? 'sign-in' : 'sign-up';
    Sentry.addBreadcrumb({
      message: `Visited ${authType} page`,
      category: 'navigation',
      level: 'info',
      data: {
        path: location.pathname,
        signUpFlag: urlSignUpFlag,
        hasSSO: searchParams.get('sso') === 'true',
        hasCode: !!searchParams.get('code'),
      },
    });
    Sentry.setTag('auth_page', authType);
  }, [location.pathname, isLogin, urlSignUpFlag, searchParams]);

  useEffect(() => {
    if (
      userLoadState === UserLoadState.None
      || userLoadState === UserLoadState.Unauthenticated
    ) {
      return;
    }

    if (userLoadState === UserLoadState.Authenticated) {
      // Don't redirect if the user is on a path that handles its own post-auth
      // navigation (e.g. signup verification → onboarding).
      const authPaths = ['/sign-up', '/sign-in', '/verification', '/get-started'];
      if (authPaths.includes(location.pathname)) {
        return;
      }

      // Get the stored path and navigate to it, or default to /my-reviews
      const storedPath = sessionStorage.getItem('preLoginPath');
      const redirectPath = storedPath || '/my-reviews';

      // Clean up the stored path
      sessionStorage.removeItem('preLoginPath');

      // Navigate to the appropriate path
      navigate(redirectPath);
    }
  }, [userLoadState, navigate, location.pathname]);

  const contributorBullets = [
    {
      icon: '💵',
      text: 'Earn cash for your selling experience & insights (up to $400 per account, 100% anonymous)',
    },
    { icon: '📞', text: 'Get consulting opportunities' },
    {
      icon: '📗',
      text: 'Access real, anonymous insights from AEs who\'ve sold to the same accounts',
    },
  ];

  const researcherBullets = [
    {
      icon: '⭐️',
      text: 'Access real, anonymous insights from AEs who\'ve sold to the same accounts',
    },
    {
      icon: '✨',
      text: 'Generate AI-driven deal-closing insights specific to your offerings',
    },
    { icon: '💬', text: 'Get consulting opportunities' },
  ];

  const trustedLogos = [
    { src: '/logos/salesforce.png', alt: 'Salesforce' },
    { src: '/logos/oracle.png', alt: 'Oracle' },
    { src: '/logos/servicenow.png', alt: 'ServiceNow' },
    { src: '/logos/mongodb.png', alt: 'MongoDB' },
    { src: '/logos/databricks.png', alt: 'Databricks' },
    { src: '/logos/docusign.png', alt: 'DocuSign' },
    { src: '/logos/sprinklr.png', alt: 'Sprinklr' },
  ];

  return (
    <>
      <SEO
        title="Kandir - Sign Up"
        description="Sign up for Kandir & join an expert network of enterprise sellers sharing real-world insight with companies that need it."
        ogImage="https://app.kandir.io/kandir_logo.png"
        twitterImage="https://app.kandir.io/kandir_logo.png"
      />
      <div className="auth-page" data-testid="page-wrapper">
        <Box className="auth-page__content" data-testid="sign-up-content">
          <Box className="auth-hero">
            <p className="auth-hero__eyebrow">
              {isLogin ? 'Welcome back' : 'Join the network'}
            </p>
            <h1 className="auth-hero__title">
              Join an expert network of enterprise sellers sharing real-world insights
            </h1>
          </Box>

          <ul className="auth-benefits">
            {(urlSignUpFlag === 'c'
              ? contributorBullets
              : researcherBullets
            ).map(({ icon, text }) => (
              <li key={text} className="auth-benefits__item">
                <span className="auth-benefits__icon">{icon}</span>
                <span className="auth-benefits__text">{text}</span>
              </li>
            ))}
          </ul>

          <Box className="auth-companies">
            <div className="auth-companies__title">
              Trusted by Account Executives at
            </div>
            <Box className="auth-companies__marquee-wrapper">
              <Box className="auth-companies__marquee">
                {[...trustedLogos, ...trustedLogos].map(
                  (logo, index) => (
                    <img
                      key={`${logo.alt}-${index}`}
                      src={logo.src}
                      alt={logo.alt}
                      className="auth-companies__logo"
                    />
                  ),
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box className="auth-page__form-panel" data-testid="auth-form-panel">
          <Box className="auth-page__form-wrapper">
            {isLogin ? <LoginCard /> : <SignUpForm />}
          </Box>
        </Box>
      </div>
    </>
  );
}

export default AuthContainer;
